import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Filter } from 'lucide-react';
import ReactSelect, {
  MultiValue,
  SingleValue,
  StylesConfig,
} from 'react-select';
import { DefaultSelectStyle } from 'components/Shared/Forms/CompactSelect/styles';
import { ColorScheme, Colors, Fonts } from 'styles/constants';

import {
  YardGateFilters,
  YardGateWarehouseOption,
  countAppliedYardGateFilters,
  validateYardGateDateRange,
} from '../../adapter';
import {
  YardGateAppointmentStatus,
  YardGateAppointmentType,
  YardGateRegistrationType,
  YardGateCatalogs,
  YardGateOption,
} from 'contracts/YardGate';
import { statusFilterOptions } from '../../statusVisuals';
import * as S from '../../styles';

interface EntryFiltersProps {
  value: YardGateFilters;
  catalogs: YardGateCatalogs;
  warehouseOptions: YardGateWarehouseOption[];
  isCatalogLoading: boolean;
  onApply: (filters: YardGateFilters) => void;
  onClear: () => void;
  rightActions?: ReactNode;
}

const fieldClass =
  'h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm ' +
  'text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 ' +
  'focus:border-[#EE7C2C] focus:ring-1 focus:ring-[#EE7C2C]';

const labelClass = 'block text-xs font-semibold text-gray-700';

const compactSelectStyle = DefaultSelectStyle as StylesConfig<
  YardGateOption,
  boolean
>;

const singleSelectStyle: StylesConfig<YardGateOption, false> = {
  ...(compactSelectStyle as StylesConfig<YardGateOption, false>),
  container: (base, state) => ({
    ...(compactSelectStyle.container?.(base, state) ?? base),
    width: '100%',
  }),
  control: (base, state) => ({
    ...(compactSelectStyle.control?.(base, state) ?? base),
    'fontSize': '14px',
    'borderColor': Colors.Gray40,
    ':hover': { borderColor: Colors.Gray40 },
  }),
  option: (base, state) => ({
    ...(compactSelectStyle.option?.(base, state) ?? base),
    fontSize: '14px',
  }),
  placeholder: (base, state) => ({
    ...(compactSelectStyle.placeholder?.(base, state) ?? base),
    fontFamily: Fonts.GilroyRegular,
  }),
  menuPortal: (base, state) => ({
    ...(compactSelectStyle.menuPortal?.(base, state) ?? base),
    zIndex: 100,
  }),
};

const multiSelectStyle: StylesConfig<YardGateOption, true> = {
  ...(compactSelectStyle as StylesConfig<YardGateOption, true>),
  container: (base, state) => ({
    ...(compactSelectStyle.container?.(base, state) ?? base),
    width: '100%',
    height: 'auto',
  }),
  control: (base, state) => ({
    ...(compactSelectStyle.control?.(base, state) ?? base),
    'height': 'auto',
    'minHeight': '32px',
    'fontSize': '14px',
    'borderColor': Colors.Gray40,
    ':hover': { borderColor: Colors.Gray40 },
  }),
  option: (base, state) => ({
    ...(compactSelectStyle.option?.(base, state) ?? base),
    fontSize: '14px',
  }),
  placeholder: (base, state) => ({
    ...(compactSelectStyle.placeholder?.(base, state) ?? base),
    fontFamily: Fonts.GilroyRegular,
  }),
  valueContainer: (base, state) => ({
    ...(compactSelectStyle.valueContainer?.(base, state) ?? base),
    flexWrap: 'wrap',
  }),
  indicatorsContainer: (base, state) => ({
    ...(compactSelectStyle.indicatorsContainer?.(base, state) ?? base),
    height: 'auto',
  }),
  menuPortal: (base, state) => ({
    ...(compactSelectStyle.menuPortal?.(base, state) ?? base),
    zIndex: 100,
  }),
};

const optionValue = (
  options: YardGateOption[],
  value: number | ''
): YardGateOption | null =>
  value === '' ? null : options.find((option) => option.value === value) ?? null;

const selectNumber = (option: SingleValue<YardGateOption>): number | '' =>
  option ? Number(option.value) : '';

export const EntryFilters = ({
  value,
  catalogs,
  warehouseOptions,
  isCatalogLoading,
  onApply,
  onClear,
  rightActions,
}: EntryFiltersProps) => {
  const [draft, setDraft] = useState(value);
  const [expanded, setExpanded] = useState(false);
  const [dateErrors, setDateErrors] = useState(
    {} as ReturnType<typeof validateYardGateDateRange>
  );

  const activeCount = useMemo(
    () => countAppliedYardGateFilters(value),
    [value]
  );

  useEffect(() => setDraft(value), [value]);

  const updateDraft = <Key extends keyof YardGateFilters>(
    field: Key,
    fieldValue: YardGateFilters[Key]
  ) => setDraft((current) => ({ ...current, [field]: fieldValue }));

  const toggleStatus = (status: YardGateAppointmentStatus) => {
    setDraft((current) => {
      const selected = current.statuses.includes(status);
      return {
        ...current,
        statuses: selected
          ? current.statuses.filter((currentStatus) => currentStatus !== status)
          : [...current.statuses, status],
      };
    });
  };

  const allStatusesSelected = statusFilterOptions.every(({ value: status }) =>
    draft.statuses.includes(status)
  );

  const toggleAllStatuses = () =>
    updateDraft(
      'statuses',
      allStatusesSelected
        ? []
        : statusFilterOptions.map(({ value: status }) => status)
    );

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateYardGateDateRange(draft);
    setDateErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onApply(draft);
  };

  return (
    <div className="-mt-2 mb-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls="yard-gate-filters-panel"
          onClick={() => setExpanded((current) => !current)}
          className="inline-flex h-9 items-center gap-2 rounded-md border border-[#EE7C2C] bg-[#EE7C2C] px-4 text-xs font-semibold text-white transition hover:brightness-95"
        >
          <Filter size={14} aria-hidden />
          Filtros
          {activeCount > 0 && (
            <span
              aria-hidden
              className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold text-[#EE7C2C]"
            >
              {activeCount}
            </span>
          )}
        </button>
        {rightActions && (
          <div className="flex flex-wrap items-center gap-3">
            {rightActions}
          </div>
        )}
      </div>

      {expanded && (
        <form
          id="yard-gate-filters-panel"
          onSubmit={submit}
          className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
        >
          <S.StatusCheckboxGroup>
            <S.StatusCheckboxLegend>Status</S.StatusCheckboxLegend>
            {statusFilterOptions.map(({ value: status, label, color }) => (
              <S.StatusCheckboxOption key={status}>
                <S.StatusCheckboxInput
                  type="checkbox"
                  checked={draft.statuses.includes(status)}
                  onChange={() => toggleStatus(status)}
                />
                <S.StatusCheckboxIcons>
                  <S.StatusCheckedIcon style={{ color }} />
                  <S.StatusUncheckedIcon />
                </S.StatusCheckboxIcons>
                {label}
              </S.StatusCheckboxOption>
            ))}
            <S.StatusCheckboxOption>
              <S.StatusCheckboxInput
                type="checkbox"
                checked={allStatusesSelected}
                onChange={toggleAllStatuses}
              />
              <S.StatusCheckboxIcons>
                <S.StatusCheckedIcon
                  style={{ color: String(ColorScheme.Text) }}
                />
                <S.StatusUncheckedIcon />
              </S.StatusCheckboxIcons>
              Todos
            </S.StatusCheckboxOption>
          </S.StatusCheckboxGroup>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <label htmlFor="yard-gate-date-from" className={labelClass}>
                Data inicial
              </label>
              <input
                id="yard-gate-date-from"
                type="date"
                className={fieldClass}
                value={draft.dateFrom}
                onChange={(event) =>
                  updateDraft('dateFrom', event.target.value)
                }
              />
              {dateErrors.dateFrom && (
                <p className="text-xs text-red-600">{dateErrors.dateFrom}</p>
              )}
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-date-to" className={labelClass}>
                Data final
              </label>
              <input
                id="yard-gate-date-to"
                type="date"
                className={fieldClass}
                value={draft.dateTo}
                onChange={(event) => updateDraft('dateTo', event.target.value)}
              />
              {dateErrors.dateTo && (
                <p className="text-xs text-red-600">{dateErrors.dateTo}</p>
              )}
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-appointment-type" className={labelClass}>
                Tipo de registro
              </label>
              <select
                id="yard-gate-appointment-type"
                className={fieldClass}
                value={draft.appointmentType}
                onChange={(event) =>
                  updateDraft(
                    'appointmentType',
                    event.target.value as YardGateRegistrationType | ''
                  )
                }
              >
                <option value="">Todos</option>
                <option value="ORDER">Ordem</option>
                <option value="STANDALONE">Avulso</option>
                <option value="HORA_CERTA">Hora Certa</option>
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-driver-cpf" className={labelClass}>
                CPF
              </label>
              <input
                id="yard-gate-driver-cpf"
                className={fieldClass}
                value={draft.driverCpf}
                onChange={(event) =>
                  updateDraft('driverCpf', event.target.value)
                }
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-driver-name" className={labelClass}>
                Motorista
              </label>
              <input
                id="yard-gate-driver-name"
                className={fieldClass}
                value={draft.driverName}
                onChange={(event) =>
                  updateDraft('driverName', event.target.value)
                }
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-phone" className={labelClass}>
                Telefone
              </label>
              <input
                id="yard-gate-phone"
                className={fieldClass}
                value={draft.phone}
                onChange={(event) =>
                  updateDraft('phone', event.target.value)
                }
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-order-number" className={labelClass}>
                Ordem
              </label>
              <input
                id="yard-gate-order-number"
                className={fieldClass}
                value={draft.orderNumber}
                onChange={(event) =>
                  updateDraft('orderNumber', event.target.value)
                }
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-plate" className={labelClass}>
                Placa
              </label>
              <input
                id="yard-gate-plate"
                className={`${fieldClass} uppercase`}
                value={draft.plate}
                onChange={(event) => updateDraft('plate', event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-order-type" className={labelClass}>
                Tipo de ordem
              </label>
              <ReactSelect<YardGateOption, false>
                inputId="yard-gate-order-type"
                aria-label="Tipo de ordem"
                isClearable
                menuPortalTarget={document.body}
                menuPlacement="top"
                styles={singleSelectStyle}
                placeholder="Todos"
                options={catalogs.orderTypes}
                value={optionValue(catalogs.orderTypes, draft.orderTypeId)}
                isDisabled={
                  isCatalogLoading || catalogs.orderTypes.length === 0
                }
                onChange={(option) =>
                  updateDraft('orderTypeId', selectNumber(option))
                }
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-carrier" className={labelClass}>
                Transportadora
              </label>
              <ReactSelect<YardGateOption, false>
                inputId="yard-gate-carrier"
                aria-label="Transportadora"
                isClearable
                menuPortalTarget={document.body}
                menuPlacement="top"
                styles={singleSelectStyle}
                placeholder="Todas"
                options={catalogs.carriers}
                value={optionValue(catalogs.carriers, draft.carrierId)}
                isDisabled={isCatalogLoading || catalogs.carriers.length === 0}
                onChange={(option) =>
                  updateDraft('carrierId', selectNumber(option))
                }
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-customer" className={labelClass}>
                Cliente
              </label>
              <ReactSelect<YardGateOption, false>
                inputId="yard-gate-customer"
                aria-label="Cliente"
                isClearable
                menuPortalTarget={document.body}
                menuPlacement="top"
                styles={singleSelectStyle}
                placeholder="Todos"
                options={catalogs.customers}
                formatOptionLabel={(option) =>
                  option.document
                    ? `${option.label} · ${option.document}`
                    : option.label
                }
                value={optionValue(catalogs.customers, draft.customerId)}
                isDisabled={isCatalogLoading || catalogs.customers.length === 0}
                onChange={(option) =>
                  updateDraft('customerId', selectNumber(option))
                }
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-warehouse" className={labelClass}>
                Armazém
              </label>
              <ReactSelect<YardGateOption, true>
                inputId="yard-gate-warehouse"
                aria-label="Armazém"
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                menuPortalTarget={document.body}
                menuPlacement="top"
                styles={multiSelectStyle}
                placeholder="Todas"
                options={warehouseOptions}
                formatOptionLabel={(option, { context }) => {
                  const document = (option as YardGateWarehouseOption).document;
                  return context === 'menu' && document ? (
                    <span title={document}>{option.label}</span>
                  ) : (
                    option.label
                  );
                }}
                value={warehouseOptions.filter((option) =>
                  draft.warehouseIds.includes(option.value)
                )}
                isDisabled={
                  isCatalogLoading || warehouseOptions.length === 0
                }
                onChange={(options) =>
                  updateDraft(
                    'warehouseIds',
                    (options as MultiValue<YardGateOption>).map(
                      ({ value: warehouseId }) => warehouseId
                    )
                  )
                }
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="yard-gate-dock" className={labelClass}>
                Doca
              </label>
              <input
                id="yard-gate-dock"
                className={`${fieldClass} uppercase`}
                value={draft.dock}
                onChange={(event) => updateDraft('dock', event.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
            <button
              type="submit"
              className="inline-flex items-center rounded-md bg-[#EE7C2C] px-5 py-2 text-xs font-semibold text-white transition hover:brightness-95"
            >
              Filtrar
            </button>
            <button
              type="button"
              onClick={onClear}
              className="inline-flex items-center rounded-md border border-slate-200 px-4 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-50"
            >
              Limpar filtros
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
